/** @file Presence pills: link status, other actors online and what they edit, recently active agents. */
import type { CatalogKey } from '@pose2d/i18n/types';
import { useStore } from 'zustand';
import { recentActors } from '../../api/sync/recentActors';
import { useT } from '../../shell/i18n/useT';
import { liveStore, type LiveStatus } from '../../stores/liveStore';
import { sessionStore } from '../../stores/sessionStore';
import { actorTone } from './actorTone';
import { useNow } from './useNow';
import './activity.css';

/** How often the recent-agent pills are re-judged against the clock (an agent leaves five minutes after its last commit). */
const RECENT_TICK_MS = 15_000;

const STATUS_KEY: Record<LiveStatus, CatalogKey> = {
  offline: 'live.offline', connecting: 'live.connecting', live: 'live.live', catchingUp: 'live.catchingUp', resyncing: 'live.resyncing',
};

/** One pill per other WebSocket actor (with what it edits), then per actor that committed recently without one. */
export function PresencePills() {
  const t = useT();
  const status = useStore(liveStore, (s) => s.status);
  const actors = useStore(liveStore, (s) => s.actors);
  const commits = useStore(liveStore, (s) => s.commits);
  const me = useStore(sessionStore, (s) => s.actor?.name ?? null);
  const online = Object.entries(actors).filter(([name]) => name !== me).sort(([a], [b]) => a.localeCompare(b));
  const now = useNow(RECENT_TICK_MS);
  const recent = recentActors(commits, now).filter((name) => name !== me && !Object.hasOwn(actors, name));
  return (
    <div className="live-pills" role="status" aria-label={t('live.pills')}>
      <span className={`live-status live-${status}`}>{t(STATUS_KEY[status])}</span>
      {online.map(([name, editing]) => (
        <span key={name} className="live-pill" data-tone={actorTone(name)}>
          {editing === null ? t('live.online', { actor: name }) : t('live.editing', { actor: name, name: editing })}
        </span>
      ))}
      {recent.map((name) => (
        <span key={name} className="live-pill live-pill-recent" data-tone={actorTone(name)}>{t('live.recent', { actor: name })}</span>
      ))}
    </div>
  );
}
